import React from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { withTranslation, withUserPreferences, withAccount } from 'components/hoc';
import styled from 'styled-components';
import { AuthGatedTheme } from '../common/AuthGatedTheme';
import './index.css';

const PrivacyStatementWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 56px 32px 96px;
  box-sizing: border-box;

  @media (max-width: 992px) {
    padding: 48px 24px 80px;
  }

  @media (max-width: 768px) {
    padding: 36px 18px 72px;
  }

  @media (max-width: 540px) {
    padding: 28px 16px 60px;
  }
`;

const Section = styled.section`
  margin-top: 1.5rem;
  line-height: 1.7;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }
`;

const StyledList = styled.ul`
  list-style-type: disc;
  padding-left: 2rem;
  
  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
  }
`;

const PrivacyContent = ({ isAuth, translate }) => {
  const textColor = isAuth ? { color: 'var(--admincat-color-grey-2)' } : {};
  const bgColor = isAuth ? { backgroundColor: 'var(--admincat-color-grey-4)' } : {};

  const getMarginLeft = (text) => {
    const length = text.length;
    if (length === 3) return '28px';  
    return '6px';
  };

  return (
    <div className="credits-disclaimer" style={bgColor}>
      <div className="disclaimer">
        <h1 style={textColor}>{translate("Privacy Statement")}</h1>

        <Section>
          <p style={textColor}>
            {translate("Thank you for your interest in our online service. The protection of your data is very important to us. We therefore apply the utmost care and state-of-the-art security standards to ensure maximum protection of your data. Below we will explain in detail, which data AdminCat is processing and how it protects this data.")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>1. {translate("The person responsible for data processing is")}:</h3>
          <p className="privacy-content" style={textColor}>
            AdminCat GmbH<br/>
            Landgraf-Karl-Str.21A <br /> 
            34131 Kassel<br/>
            Germany
          </p>
          <p className="privacy-content" style={textColor}>
            <span>{translate("E-Mail")}:</span> <a href="mailto:admin@admincat.net" style={{ fontSize: '18px' }}>admin@admincat.net</a>
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>2. {translate("Legal basis")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("The provision of our services requires the processing of data and takes place in accordance with the statutory provisions. For data protection, the General Data Protection Regulation (GDPR), the applicable Federal Data Protection Act (BDSG), the Telemedia Act (TMG) and telecommunications secrecy are decisive. AdminCat's services are hosted exclusively on servers in Germany (Frankfurt am Main).")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>3. {translate("Terms")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("The legislator requires that personal data be processed in a lawful manner, in good faith and in a manner that is understandable for the person concerned ('lawfulness, processing in good faith, transparency'). To ensure this, we inform you about the individual legal definitions that are also used in this data protection declaration")}:
          </p>
          
          <div>
            <h4 style={textColor}>(a) {translate("Personal data")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"Personal data" is all information that relates to an identified or identifiable natural person (hereinafter "data subject"); A natural person is regarded as identifiable who can be identified directly or indirectly, in particular by means of assignment to an identifier such as a name, an identification number, location data, an online identifier or one or more special features that express the physical, physiological, genetic, psychological, economic, cultural or social identity of this natural person.')}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(b) {translate("Processing")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"Processing" is any process carried out, with or without the help of automated processes, or any such series of processes in connection with personal data such as the collection, recording, organization, ordering, storage, adaptation or change, reading out, querying, use, disclosure by transmission, distribution or any other form of provision, comparison or linking, restriction, deletion or destruction.')}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(c) {translate("Restriction of processing")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"Restriction of processing" is the marking of stored personal data with the aim of restricting their future processing.')}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(d) {translate("Profiling")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"Profiling" is any type of automated processing of personal data that consists of using this personal data to evaluate certain personal aspects relating to a natural person, in particular aspects relating to work performance, economic situation, health,to analyze or predict personal preferences, interests, reliability, behavior, whereabouts or relocation of this natural person.')}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(e) {translate("Pseudonymization")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"Pseudonymization" is the processing of personal data in such a way that the personal data can no longer be assigned to a specific data subject without the use of additional information, provided that this additional information is stored separately and is subject to technical and organizational measures that ensure that the personal data cannot be assigned to an identified or identifiable natural person.')}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(f) {translate("File system")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"File system" is any structured collection of personal data that is accessible according to certain criteria, regardless of whether this collection is managed centrally, decentralized or according to functional or geographical criteria')}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(g) {translate('Person responsible')}</h4>
            <p className="privacy-content" style={textColor}>
              {translate('"Person responsible" is a natural or legal person, authority, institution or other body that alone or jointly with others decides on the purposes and means of processing personal data; If the purposes and means of this processing are specified by Union law or the law of the member states, the person responsible or the specific criteria for his appointment can be provided for in accordance with Union law or the law of the member states.')}
            </p>
          </div>
        </Section>

        <Section>
          <h3 style={textColor}>4. {translate("Access Data")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("AdminCat collects data and information, some of which is personal data, each time the website is accessed. This data is stored in the server log files. The following can be recorded")}:
          </p>
          <StyledList style={textColor}>
            <li>{translate("Web request and interaction with a service, e.g. PayPal")}</li>
            <li>{translate("The Internet Protocol address (IP address)")}</li>
            <li>{translate("Browser type and language of the browser")}</li>
            <li>{translate("Date and time of access to our website")}</li>
            <li>{translate("Time zone difference to Greenwich Mean Time (GMT)")}</li>
            <li>{translate("The email address of a Space, if a user is logged in there")}</li>
            <li>{translate("Access status/HTTPS status code")}</li>
          </StyledList>
          <p className="privacy-content" style={textColor}>
            {translate("The legal basis for data processing is Art. 6 (1) p. 1 lit. f GDPR. Our legitimate interest is based on the following purposes for data collection")}:
          </p>
          <StyledList style={textColor}>
            <li>{translate("Ensuring a smooth connection to the website")}</li>
            <li>{translate("Ensuring a convenient use of our website")}</li>
            <li>{translate("Evaluation of system security and stability, and")}</li>
            <li>{translate("For other administrative purposes")}</li>
          </StyledList>
          <p className="privacy-content" style={textColor}>
            {translate("After the end of the communication process, the collected information is only evaluated for statistical purposes and in anonymized form. Under no circumstances do we use the data collected for the purpose of drawing conclusions about your person.")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>5. {translate("Cookies")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("AdminCat doesn't use any cookie. Therefore we also don't provide an integrated cookie consent tool on our site, which usually allows you to administrate your opt-in choices related to certain cookies and their features.")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>6. {translate("Inventory and payment data")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("All you need to create a user account is an e-mail address. We use this to send you a link to confirm your registration and to reset your password. We do not collect any other inventory data. Your email address is not passed on to third parties, nor do we use third-party data to enrich your email address with this data and create a profile about you.")}
            <br /><br />
            {translate("Although you can voluntarily enter or view extensive data in a Space to which you have been granted access (about people, addresses, health data, contract data and much more), the members of these Spaces are themselves responsible for the data managed there. AdminCat employees have virtually no access to this data (see explanations under 7. Content data).")}
            <br /><br />
            {translate("You can pay for our services, such as the paid subscription, via payment services such as PayPal. The amount, the subscription period and a PayPal reference in connection with your user account are stored in our database for you as confirmation and overview for each payment transaction processed via this service, but only in double-encrypted form, so that our employees have no access to this data.")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>7. {translate("Content data (email, documents and recorded data)")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("AdminCat manages emails, documents and extensive master and transaction data for you (insofar as you decide to use it). Only you as the user should have sovereignty and full control over this data. That is why we offer you maximum protection with our security concept")}:
          </p>
          <StyledList style={textColor}>
            <li>
              {translate("AdminCat is hosted on an AWS server in Frankfurt am Main (Germany) and is therefore subject to the strictest EU data protection regulations; AWS has ISO/IEC certifications 27001:2022, 27017:2015 and 27018:2019")}
            </li>
            <li>
              {translate("We secure communication with your end device using the modern TLS 1.3 (Transport Layer Security) standard, which means that all data is transmitted in encrypted form and protected against manipulation and unauthorized access by third parties during transmission")}
            </li>
            <li>
              {translate("We counter potential attackers with high password standards, automatic session termination after 1 hour of inactivity and optional two-factor authentication (2FA)")}
            </li>
            <li>
              {translate("In addition to AWS encryption, your content data is stored on a second encrypted database and access to it is only permitted using keys assigned to your login data")}
            </li>
            <li>
              {translate("The column names in the tables of our database are still masked in such a way that an unauthorized reader can hardly interpret or assign the content of the columns")}
            </li>
            <li>
              {translate("In rare cases, complete encryption of the data is associated with unacceptable performance losses, e.g. for full-text searches in documents uploaded by you. Therefore, instead of secondary encryption, rigorous text splitting is used here, which means that the specific text content remains protected for you")}
            </li>
          </StyledList>
        </Section>

        <Section>
          <h3 style={textColor}>8. {translate("Service contact (contact form, email, phone)")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("You can contact us via a contact form (accessible from your user account), by email or by phone. These communication channels are generally open to you for your questions. We are also very interested in your feedback, what you like and your suggestions for improvements.")}
            <br /><br />
            {translate("Personal data is not required, unless you may wish to be called back or receive an answer by email. To analyze errors on the site, we ask you for a description of the error as detailed as possible, but we will never ask for personal data or even login data. Seven days after each request has been completed, we delete all related messages in our systems. Your telephone number will remain on a call list on one of our telephones for one month without a name reference until it is deleted; no additional storage or further processing will take place.")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>9. {translate("Disclosure of data")}</h3>
          <p className="privacy-content" style={textColor}>
            {translate("AdminCat does not pass on any data to third parties unless there is a legal obligation to do so or the transfer of the data is absolutely necessary for the provision of a service requested by the user (Art. 6 para. 1 lit. f GDPR). This section provides information about such services, our partners involved in them and what data is used for what purpose. Users who make use of these services on our website tacitly consent to the corresponding processing.")}
            <br /><br />
            {translate("AWS is our hosting partner, on whose servers our website is operated in Frankfurt am Main (Germany). This means that AWS is of course bound by the strict European data protection laws (GDPR), and under no circumstances may it access your data or pass data on to third parties. The open source software AWStats is used on its servers to analyze log files, which means that no cookies are required by your browser. AWS only uses anonymized IP address data for this purpose. This data cannot be assigned to a specific person and is not forwarded to third parties.")}
            <br /><br />
            {translate("If you take out a subscription on our website, you can use the service provider PayPal for payment. To do this, you need a corresponding user account with PayPal, for which you agree to the terms and conditions and privacy policy accordingly. For each payment process, AdminCat only transmits the absolutely necessary data such as provider, amount and currency and receives the status and a PayPal reference number of the payment from PayPal.")}
            <br /><br />
            {translate("You will find embedded videos on our pages that are provided by the YouTube service. YouTube is operated by Google Ireland Limited, Gordon House, 4 Barrow St, Dublin, D04 E5W5, Ireland. When you play a video, a direct connection to the Google servers is established. Your IP address and the address of the page from which you are watching the video will be transmitted. YouTube also uses a cookie that collects further user data in order to offer matching videos. If you have a Google user account and are currently logged in, YouTube can link data, such as the video you clicked on, to your Google user account. If you do not wish this to happen, you must log out of Google.")}
            <br /><br />
            {translate("We also use Google Fonts on our website, that are loaded onto your device from the Google Fonts servers by your browser. In this way, we can consistently present our texts in an appealing form (Art. 6 para. 1 lit. f GDPR). You can find more information about Google's data protection at")}
            {' '}<a style={{ fontSize: '18px' }} href='https://policies.google.com/privacy' target='_blank' rel='noopener noreferrer'>{translate('https://policies.google.com/privacy.')}</a>
            <br /><br />
            {translate("AdminCat does not use social media plugins (e.g. for Facebook, Instagram or X). We also do not use tracking and analysis services such as Google Analytics. Furthermore, we do not use user profiling and automated decisions based on this, nor do we display advertising or send newsletters.")}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>10. {translate("Data protection rights of the data subject")}</h3>
          
          <div>
            <h4 style={textColor}>(a) {translate("Conditions for consent")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("If the processing of personal data is based on consent given, you have the right to revoke your consent at any time in accordance with")} <b>{translate("Art. 7 Paragraph 3 GDPR")}</b>. {translate("Revoking your consent does not affect the legality of the processing carried out on the basis of your consent up to the point of revocation.")}
              <br />
              {translate("You can contact us at any time to exercise your right of withdrawal.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(b) {translate("Right of access by the data subject")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("In accordance with")} <b>{translate("Art. 15 GDPR")}</b>, {translate("you have the right to request a confirmation from AdminCat as to whether we are processing personal data related to you. If that is the case, you have the right to information about this personal data, the details of the processing and comprehensive information about your rights. You can request confirmation at any time using the contact details above.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(c) {translate("Right to rectification")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("In accordance with")} <b>{translate("Art. 16 GDPR")}</b>, {translate("you have the right to demand immediate rectification or completion of personal data about you that is administered by us.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(d) {translate("Right to erasure (right to be forgotten)")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("In accordance with")} <b>{translate("Art. 17 GDPR")}</b>, {translate("you have the right to request the immediate deletion of personal data that AdminCat has stored about you, provided that it is no longer necessary for the purposes for which it was collected or otherwise processed.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(e) {translate("Right to restriction of processing")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("According to")} <b>{translate("Art. 18 GDPR")}</b>, {translate("any data subject affected by the processing of personal data, has the right to require the person responsible for restricting the processing, if such information is incorrect, the processing illegitimate or inexpedient, or if an objection according to")} <b>{translate("Art. 21 paragraph 1")}</b> {translate("is present.")}
              <br /><br />
              {translate("In order to exercise the right to restriction of processing, the data subject can contact us at any time using the contact details given above.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(f) {translate("Notification obligation to receiving third parties")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("If the person responsible is sharing personal data of the data subject with third parties, according to")} <b>{translate("Art. 19 GDPR")}</b>, {translate("he must notify these recipients when personal data have been corrected or deleted")}. {translate("you have the right to ask companies to whom this information was sent")}.
            </p>
          </div>

          <div>
            <h4 style={textColor}>(g) {translate("Right to data portability")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("If you have provided a company with personal data in an automated process, you have the right, in accordance with")} <b>{translate("Art. 20 GDPR")}</b>, {translate("to receive the personal data stored there about you in a structured, common and machine-readable format or to request that it be transmitted to another person responsible. At least as far as this is technically feasible.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(h) {translate("Right to object")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("In accordance with")} <b>{translate("Art. 21 GDPR")}</b>, {translate("you have the right to object to processing of personal data at AdminCat at any time, unless there are compelling legitimate grounds for the processing.")}
              <br /><br />
              {translate("To exercise the right to object you can contact the relevant person responsible at any time.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(i) {translate("Automated individual decision-making, including profiling")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("According to")} <b>{translate("Art. 22 GDPR")}</b>, {translate("every person affected by the processing of personal data has the right not to be subject to a decision based on automated processing - including profiling. Such processing is not used at AdminCat.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(j) {translate("Communication of a personal data breach to the data subject")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("If a company saves your personal data, it must inform you immediately in accordance with")} <b>{translate("Art. 34 GDPR")}</b>. {translate("If the protection of this data has been breached, we will inform you about security-relevant incidents in which the security of your data stored at AdminCat could be violated by email and on our website and give you tips for suitable measures.")}
            </p>
          </div>

          <div>
            <h4 style={textColor}>(k) {translate("Right to lodge a complaint with a supervisory authority")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("According to")} <b>{translate("Art. 77 GDPR")}</b>, {translate("you have the right to complain to a supervisory authority. The responsible supervisory authorities are")}:
            </p>
            <p className="privacy-content" style={textColor}>
              Der Bundesbeauftragte für den Datenschutz <br/>
              und die Informationsfreiheit (BfDI) <br />
              Husarenstr. 30<br /> 
              53117 Bonn<br/> 
              <span>{translate('Germany')}</span> 
            </p>
            <p className="privacy-content" style={textColor}>
              {translate("Affected persons have the right to complain to the competent technical supervisory authority in the event of data protection problems. The appropriate authority for us is")}:
            </p>
            <p className="privacy-content" style={textColor}>
              Der Hessische Beauftragte für  <br/>
              Datenschutz und Informationsfreiheit <br />
              Postfach 3163<br /> 
              65021 Wiesbaden<br/> 
              <span>{translate('Germany')}</span> 
            </p>
            <p className="privacy-content" style={textColor}>
              <span>{translate("E-Mail")}:</span> <a href="mailto:poststelle@datenschutz.hessen.de" style={{ fontSize: '18px' }}>poststelle@datenschutz.hessen.de</a>
            </p>
            <p className="privacy-content" style={textColor}>
              <span>{translate('Phone')}</span>: +49 611 1408 - 0<br/>
              <span>{translate('Fax')}:</span>
              <span style={{ display: 'inline-block', marginLeft: getMarginLeft(translate('Fax')) }}></span>
              +49 611 1408 - 900 
            </p>
          </div>

          <div>
            <h4 style={textColor}>{translate("Topicality and changes to this privacy statement")}</h4>
            <p className="privacy-content" style={textColor}>
              {translate("This privacy statement is currently valid and is dated June 2024. Due to the further development of AdminCat or due to changed legal or official requirements, it may be necessary to change this privacy statement. In this case, we will inform our users in advance by email.")}
            </p>
          </div>
        </Section>
      </div>
    </div>
  );
};

const PrivacyStatement = (props) => {
  const { tr, account: { user: { isAuth } } } = props;
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  // Use tr for authenticated users, t for non-authenticated users
  const translate = isAuth ? tr : t;

  return (
    <AuthGatedTheme isAuth={isAuth}>
      <PrivacyStatementWrapper>
        <PrivacyContent isAuth={isAuth} translate={translate} />
      </PrivacyStatementWrapper>
    </AuthGatedTheme>
  );
};

export default compose(withTranslation, withUserPreferences, withAccount)(PrivacyStatement);